import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, MessageSquare, Target, TrendingUp, GitCompare, Users,
  Crown, MessagesSquare, LogOut, Menu, X, Sparkles, Flame, ClipboardCheck, GraduationCap, Gift, Send, Share2, ShieldCheck, BookOpenCheck,
} from "lucide-react";
import { PrivacyGate } from "@/components/PrivacyGate";

const STUDENT_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/assessment", label: "AI Assessment", icon: ClipboardCheck },
  { to: "/prep", label: "Test Preparation", icon: BookOpenCheck, prefix: "/prep" },
  { to: "/career-graph", label: "Career Graph", icon: Share2 },
  { to: "/chat", label: "AI Career Coach", icon: MessageSquare },
  { to: "/predictor/college", label: "College Predictor", icon: Target },
  { to: "/predictor/rank", label: "Rank Predictor", icon: TrendingUp },
  { to: "/compare", label: "Compare Colleges", icon: GitCompare },
  { to: "/scholarships", label: "Scholarships", icon: GraduationCap },
  { to: "/apply-colleges", label: "Apply to Colleges", icon: Send },
  { to: "/counsellors", label: "Find Counsellors", icon: Users },
  { to: "/community", label: "Community", icon: MessagesSquare },
  { to: "/referrals", label: "Refer & Earn", icon: Gift },
  { to: "/pricing", label: "Upgrade Plan", icon: Crown },
  { to: "/dashboard/privacy", label: "Privacy & Data", icon: ShieldCheck },
];
const COUNSELLOR_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/community", label: "Community", icon: MessagesSquare },
  { to: "/dashboard/privacy", label: "Privacy & Data", icon: ShieldCheck },
];
const ADMIN_NAV = [
  { to: "/dashboard", label: "Command Center", icon: LayoutDashboard },
  { to: "/admin/colleges", label: "Recommended Colleges", icon: GraduationCap },
  { to: "/counsellors", label: "Counsellors", icon: Users },
  { to: "/community", label: "Community", icon: MessagesSquare },
  { to: "/admin/privacy", label: "DPDP & Privacy", icon: ShieldCheck },
  { to: "/admin/mentors", label: "Mentor Profiles", icon: GraduationCap },
  { to: "/admin/test-prep", label: "Test Prep Content", icon: BookOpenCheck },
];
const PARENT_NAV = [
  { to: "/dashboard", label: "My Child's Career", icon: Users },
  { to: "/counsellors", label: "Find Counsellors", icon: GraduationCap },
  { to: "/dashboard/privacy", label: "Privacy & Data", icon: ShieldCheck },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = user?.role === "admin" ? ADMIN_NAV : user?.role === "counsellor" ? COUNSELLOR_NAV : user?.role === "parent" ? PARENT_NAV : STUDENT_NAV;
  const plan = user?.plan_effective || user?.plan || "free";

  const NavLinks = () => (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => {
        const active = location.pathname === item.to || (item.prefix && location.pathname.startsWith(item.prefix));
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
              active
                ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/20"
                : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`}
          >
            <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 glass flex items-center justify-between px-4 py-3">
        <Logo />
        <div className="flex items-center gap-2">
          <NotificationBell testId="notification-bell-mobile" />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-white p-2">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 z-30 h-screen w-72 shrink-0 flex-col justify-between border-r border-white/8 bg-[#0D1420] p-5 transition-transform lg:translate-x-0 lg:flex ${
            open ? "translate-x-0 flex" : "-translate-x-full hidden"
          }`}
        >
          <div>
            <div className="hidden lg:flex items-center justify-between mb-8">
              <Logo />
              <NotificationBell testId="notification-bell-desktop" align="left" />
            </div>
            <div className="mb-6 rounded-2xl border border-white/8 bg-gradient-to-br from-[#131C2E] to-[#0F1626] p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center font-bold text-[#041016]">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
                  <p className="truncate text-xs text-slate-400 capitalize">{user?.role}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {user?.role === "student" && (
                  <span data-testid="sidebar-plan-badge" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                    plan === "free" ? "bg-slate-700/60 text-slate-300" : "bg-amber-400/15 text-amber-300"
                  }`}>
                    {plan !== "free" && <Crown className="h-3 w-3" />} {plan} plan
                  </span>
                )}
                {user?.role === "student" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2.5 py-1 text-[11px] font-semibold text-orange-300">
                    <Flame className="h-3 w-3" /> {user?.streak || 1}d
                  </span>
                )}
              </div>
            </div>
            <NavLinks />
          </div>

          <div className="space-y-2">
            {plan === "free" && user?.role === "student" && (
              <Link to="/pricing" data-testid="sidebar-upgrade-cta"
                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2.5 text-sm font-bold text-[#1a1206] transition-transform hover:scale-[1.02]">
                <Sparkles className="h-4 w-4" /> Go Premium
              </Link>
            )}
            <button
              data-testid="logout-btn"
              onClick={async () => { navigate("/", { replace: true }); await logout(); }}
              className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-slate-400 transition-all hover:bg-rose-500/10 hover:text-rose-300"
            >
              <LogOut className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /> Log out
            </button>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-20 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Main */}
        <main className="flex-1 min-w-0 min-h-screen"><PrivacyGate />{children}</main>
      </div>
    </div>
  );
}
